import {call, put, select, takeEvery, takeLatest} from "redux-saga/effects";

import {LOGOUT_FINISHED} from "@atg-global-shared/user/userActionTypes";
import {AuthSelectors} from "@atg-shared/auth";
import log, {serializeError} from "@atg-shared/log";
import {
    type ApolloQueryService,
    getApolloQueryService,
} from "@atg-tillsammans/apollo-data-access";
import {
    MyTeamsDocument,
    type MyTeamsQuery,
    type MyTeamsQueryVariables,
} from "@atg-tillsammans/graphql/query/__generated__/teamQueries.graphql.generated";

import {ActionType, TeamMemberActions} from "./teamMember.actions";
import * as TeamMemberSelectors from "./teamMember.selectors";

import type {FetchMemberTeamsPayload} from "./teamMember.types";
import type {PayloadAction} from "@reduxjs/toolkit";
import type {SagaIterator} from "redux-saga";

// ─── Constants ────────────────────────────────────────────────────────────────

const TTL = 60_000;

// ─── Saga ─────────────────────────────────────────────────────────────────────

/**
 * Fetch member teams, respecting the Redux TTL cache.
 * The ApolloQueryService is injected so the saga stays unit-testable.
 */
export function* fetchMemberTeamsSaga(
    action: PayloadAction<FetchMemberTeamsPayload>,
): SagaIterator {
    const service: ApolloQueryService | null = yield call(getApolloQueryService);

    if (!service) {
        log.warn("[fetchMemberTeamsSaga] Apollo client not initialized");
        return;
    }

    // Check whether the Redux cache is still fresh
    const cachedAt = yield select(TeamMemberSelectors.selectTeamsCachedAt);

    const isLoggedIn = yield select(AuthSelectors.isNormalLogin);

    const isStale = cachedAt === null || Date.now() - cachedAt > TTL;

    const shouldForceNetwork = action.payload?.refreshTeams ?? false;

    if (!(isStale || shouldForceNetwork)) return; // nothing to do

    const {data, error} = yield call(
        [service, service.query<MyTeamsQuery, MyTeamsQueryVariables>],
        MyTeamsDocument,
        {
            variables: {
                refreshTeams: action.payload?.refreshTeams ?? false,
                isLoggedIn,
            },
            forceNetwork: action.payload?.refreshTeams ?? false,
        },
    );

    if (error) {
        log.error("[fetchMemberTeamsSaga]", {
            error: serializeError(error),
        });
        return;
    }

    if (data) {
        yield put(TeamMemberActions.setMemberTeams({data, cachedAt: Date.now()}));
    }
}

/**
 * Force a fresh network fetch, bypassing Apollo's cache (e.g. after a mutation).
 */
export function* invalidateAndRefetchMemberTeamsSaga(
    action: PayloadAction<FetchMemberTeamsPayload>,
): SagaIterator {
    const service: ApolloQueryService | null = yield call(getApolloQueryService);

    if (!service) {
        log.warn("[fetchMemberTeamsSaga] Apollo client not initialized");
        return;
    }

    yield put(TeamMemberActions.invalidateMemberTeams());
    service.evict(MyTeamsDocument); // evict from Apollo cache
    yield call(fetchMemberTeamsSaga, action);
}

/**
 * Reset member teams in both redux and Apollo's cache (e.g. after logout event)
 */
export function* resetMemberTeamsSaga(): SagaIterator {
    const service: ApolloQueryService | null = yield call(getApolloQueryService);

    if (!service) {
        log.warn("[fetchMemberTeamsSaga] Apollo client not initialized");
        return;
    }

    service.evict(MyTeamsDocument); // evict from Apollo cache
    yield put(TeamMemberActions.resetMemberTeams());
}

export default function* memberTeamsSaga(): SagaIterator {
    yield takeLatest(ActionType.FETCH_MEMBER_TEAMS, fetchMemberTeamsSaga);
    yield takeEvery(
        ActionType.INVALIDATE_AND_REFETCH_MEMBER_TEAMS,
        invalidateAndRefetchMemberTeamsSaga,
    );
    yield takeLatest(LOGOUT_FINISHED, resetMemberTeamsSaga);
}
